import React from 'react';
import classNames from 'classnames';
import { testId } from '@jwp/ott-common/src/utils/common';
import { useNavigate, useLocation } from 'react-router';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { renderToString } from 'react-dom/server';
import type { PlaylistItem, videoDetails } from '@jwp/ott-common/types/playlist';

import CollapsibleText from '../CollapsibleText/CollapsibleText';
import { useTheme } from '../../../../../ThemeContext';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import styles from './VideoDetails.module.scss';
import ErrorPage from '../ErrorPage/ErrorPage';

type Props = {
  title: string;
  description: string;
  primaryMetadata: React.ReactNode;
  movieGenreMetadata?: string[];
  secondaryMetadata?: React.ReactNode;
  image?: string;
  startWatchingButton: React.ReactNode;
  shareButton: React.ReactNode;
  favoriteButton?: React.ReactNode;
  trailerButton?: React.ReactNode;
  children: React.ReactNode;
  item: PlaylistItem | videoDetails;
};

const VideoDetails: React.VFC<Props> = ({
  title,
  description,
  primaryMetadata,
  secondaryMetadata,
  movieGenreMetadata,
  image,
  startWatchingButton,
  shareButton,
  favoriteButton,
  trailerButton,
  children,
  item,
}) => {
  const breakpoint: Breakpoint = useBreakpoint();
  const isMobile = breakpoint === Breakpoint.xs;
  const alt = ''; // intentionally empty for a11y, because adjacent text alternative
  const params = new URLSearchParams(window.location.search);
  const channel = params.get('channel');

  const { epgNoProgramText, epgEmptySlotText, epgDisabledMessage, heroBanner } = useUIControls();
  const { landscapeThumbnail } = useAppInfo();
  const { isEpgEnable } = useAppConfig();

  const navigate = useNavigate();
  const location = useLocation();
  const { universalStyles } = useTheme();

  const metadataString = renderToString(primaryMetadata);

  const showMoreClickHandler = () => {
    navigate(modalURLFromLocation(location, 'show-more-details'), {
      state: { item: item, primaryMetadata: metadataString },
    });
  };

  if (channel && !isEpgEnable) {
    return <ErrorPage title={epgDisabledMessage} />;
  }

  const getBannerOption = (option: string | undefined) => {
    switch (option) {
      case 'option1':
        return styles.posterOption1;
      case 'option2':
        return styles.posterOption2;
      case 'option3':
        return styles.posterOption3;
      default:
        return styles.posterOption1;
    }
  };

  return (
    <div data-testid={testId('cinema-layout')}>
      <header className={styles.video} data-testid={testId('video-details')} id="video-details">
        <div className={classNames(styles.main, styles.mainPadding)}>
          <img className={styles.posterOption3} src={image ?? landscapeThumbnail} alt={alt} />
          <div className={styles.info}>
            <h1 className={styles.title} style={universalStyles.videoDetailsTitle}>
              {title}
            </h1>
            {title == epgNoProgramText || title == epgEmptySlotText ? null : (
              <div className={styles.metaContainer}>
                <div className={styles.primaryMetadata}>{primaryMetadata}</div>
                {secondaryMetadata && <div className={styles.secondaryMetadata}>{secondaryMetadata}</div>}
                {movieGenreMetadata && movieGenreMetadata.length > 0 && (
                  <div className={styles.genresContainer}>
                    {movieGenreMetadata &&
                      movieGenreMetadata.length > 0 &&
                      movieGenreMetadata.map((item, index) => (
                        <div className={styles.genres} key={index}>
                          <span style={universalStyles.genresChipContainerText}>{item}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            <CollapsibleText
              item={item}
              text={description}
              className={styles.description}
              maxHeight={isMobile ? 60 : 120}
              showMoreClickHandler={showMoreClickHandler}
            />
            {item && item?.audio_language && (
              <div className={styles.audioLanguage}>
                <span>Audio Languages:</span>
                <span>{item.audio_language}</span>
              </div>
            )}
            {title == epgNoProgramText || title == epgEmptySlotText ? null : (
              <div className={styles.buttonBar}>
                {startWatchingButton}
                {trailerButton}
                {favoriteButton}
                {shareButton}
              </div>
            )}
          </div>
        </div>
      </header>
      {children}
    </div>
  );
};

export default VideoDetails;
